
import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppContext } from '../../App.jsx';
import { collection, query, where, onSnapshot } from 'firebase/firestore';
import { CHATS_COLLECTION } from '../../config/constants';
import AuthLoadingScreen from '../AuthLoadingScreen.jsx';
import ClientServiceCard from '../../components/client/ClientServiceCard.jsx';
import ConfirmationModal from '../../components/common/ConfirmationModal.jsx';
import { FileText, History, ArrowLeft, Info, List } from 'lucide-react';

const ClientAppointmentScreen = () => {
    const { db, user, cancelService } = useAppContext();
    const navigate = useNavigate();

    const [allServices, setAllServices] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [serviceToCancel, setServiceToCancel] = useState(null);
    const [showHistory, setShowHistory] = useState(false);
    const [rotation, setRotation] = useState(0);

    useEffect(() => {
        if (!db || !user) {
            setIsLoading(false);
            return () => {};
        }

        const q = query(collection(db, CHATS_COLLECTION), where("participants", "array-contains", user.uid));

        const unsubscribe = onSnapshot(q, (snapshot) => {
            const chatsData = snapshot.docs
                .map(doc => ({ id: doc.id, ...doc.data() }))
                .filter(service => service.clientId === user.uid);

            chatsData.sort((a, b) => (b.lastMessageTimestamp?.seconds || 0) - (a.lastMessageTimestamp?.seconds || 0));
            setAllServices(chatsData);
            setIsLoading(false);
        }, (error) => {
            console.error("Erro ao buscar serviços do cliente:", error);
            setIsLoading(false);
        });

        return () => unsubscribe();
    }, [db, user]);

    const filteredServices = useMemo(() => {
        const historyStatus = ['Pago', 'Finalizado', 'Cancelado'];
        if (showHistory) {
            return allServices.filter(service => historyStatus.includes(service.status));
        } else {
            return allServices.filter(service => !historyStatus.includes(service.status));
        }
    }, [allServices, showHistory]);

    const handleOpenCancelModal = (service) => {
        if (service.quote?.status === 'accepted') {
            alert("Proposta de Orçamento aceita. Para cancelar, por favor, entre em contato com o prestador através da conversa.");
            return;
        }
        setServiceToCancel(service);
    };

    const handleConfirmCancel = () => {
        if (serviceToCancel) {
            cancelService(serviceToCancel.id);
        }
        setServiceToCancel(null);
    };

    const handleToggleHistory = () => {
        setShowHistory(prev => !prev);
        setRotation(prev => prev + 360);
    };

    const screenTitle = showHistory ? "Histórico de Serviços" : "Meus Serviços";

    if (isLoading) {
        return <AuthLoadingScreen message="Carregando seus serviços..." />;
    }

    return (
        <div className="p-4 h-full flex flex-col bg-gray-50">
            <div className="flex justify-between items-center mb-6 px-1 text-left">
                <div className="flex items-center">
                    {showHistory ? (
                        <button onClick={handleToggleHistory} className="p-2 rounded-full hover:bg-gray-200 mr-2" title="Voltar">
                            <ArrowLeft className="w-6 h-6 text-gray-700" />
                        </button>
                    ) : (
                        <List className="mr-3 text-gray-400" size={24} />
                    )}
                    <h1 className="text-2xl font-bold text-gray-800">{screenTitle}</h1>
                </div>
                <div className="flex items-center space-x-2">
                    {!showHistory && (
                        <button
                            onClick={() => navigate('/proposals')}
                            title="Ver Todos Orçamentos"
                            className="p-2 rounded-full bg-white shadow-sm text-blue-600 border border-blue-50 hover:bg-blue-50 transition-all"
                        >
                            <FileText size={20} />
                        </button>
                    )}
                    <button
                        onClick={handleToggleHistory}
                        title={showHistory ? "Ver Ativos" : "Ver Histórico"}
                        className={`p-2 rounded-full transition-all ${showHistory ? 'bg-blue-100 text-blue-600' : 'bg-white shadow-sm text-gray-400 border border-gray-100'}`}>
                        <History
                            className="w-5 h-5 transition-transform duration-500"
                            style={{ transform: `rotate(${rotation}deg)` }}
                        />
                    </button>
                </div>
            </div>

            <div className="space-y-4 flex-grow overflow-y-auto pb-20 pr-1 text-left">
                {filteredServices.length > 0 ? (
                    filteredServices.map(service => (
                        <ClientServiceCard
                            key={service.id}
                            service={service}
                            onCancel={handleOpenCancelModal}
                        />
                    ))
                ) : (
                    <div className="text-center py-16 bg-white rounded-[2rem] border-2 border-dashed border-gray-100 px-6">
                        <Info size={40} className="text-gray-200 mx-auto mb-3" />
                        <p className="text-gray-500 font-bold">Nenhum serviço encontrado</p>
                    </div>
                )}
            </div>

            {serviceToCancel && (
                <ConfirmationModal
                    title="Cancelar Serviço"
                    message="Deseja realmente cancelar esta solicitação?"
                    confirmText="Sim, Cancelar"
                    onConfirm={handleConfirmCancel}
                    onClose={() => setServiceToCancel(null)}
                />
            )}
        </div>
    );
};

export default ClientAppointmentScreen;
